import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { Search, MoreVertical, Loader2, ArrowLeft, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { format } from "date-fns";
import {
  listKnowledgeSourcesServer,
  deleteKnowledgeDocumentServer,
  getKnowledgeSourceServer,
  updateKnowledgeDocumentServer,
  updateKnowledgeSourceServer,
  type KnowledgeDocument,
  type KnowledgeSource,
  type SourceListResponse,
  type SourceDetail,
} from "@/server/knowledge-sources";
import {
  knowledgeDialogCancelButtonClass,
  knowledgeDialogContentClass,
  knowledgeDialogDangerButtonClass,
  knowledgeDialogFooterClass,
  knowledgeDialogHeaderClass,
  knowledgeDialogTitleClass,
} from "@/components/knowledge/dialog-styles";

export const Route = createFileRoute("/workspace/$workspaceSlug/knowledge/snippets")({
  loader: async ({ parentMatchPromise }) => {
    const parent = await parentMatchPromise;
    const workspaceId = parent?.loaderData?.workspaceId;

    if (!workspaceId) {
      throw new Error("workspaceId not found in parent loader");
    }

    return { workspaceId };
  },
  component: KnowledgeSnippetsPage,
});

const statusColors = {
  pending: "bg-yellow-100 text-yellow-800",
  processing: "bg-blue-100 text-blue-800",
  completed: "bg-green-100 text-green-800",
  failed: "bg-red-100 text-red-800",
  deleted: "bg-gray-100 text-gray-800",
};

interface SnippetDocumentRow {
  source: KnowledgeSource;
  document: KnowledgeDocument;
}

interface SnippetDeleteTarget {
  sourceId: string;
  documentId: string;
}

function KnowledgeSnippetsPage() {
  const { t } = useTranslation("knowledge");
  const queryClient = useQueryClient();
  const { workspaceId } = Route.useLoaderData();

  const [searchQuery, setSearchQuery] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<SnippetDeleteTarget | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [viewSourceId, setViewSourceId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [editTags, setEditTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState("");
  const [editDocuments, setEditDocuments] = useState<
    Record<string, { title: string; content: string }>
  >({});
  const [isSaving, setIsSaving] = useState(false);

  const { data, isLoading, error } = useQuery<SourceListResponse>({
    queryKey: ["knowledge-sources", workspaceId, "snippet"],
    queryFn: async () => {
      const result = await listKnowledgeSourcesServer({
        data: {
          workspaceId,
          sourceType: "snippet",
          page: 1,
          pageSize: 50,
        },
      });
      return result as SourceListResponse;
    },
  });

  const sources: KnowledgeSource[] = data?.items ?? [];

  const detailQueries = useQueries({
    queries: sources.map((source) => ({
      queryKey: ["knowledge-source-detail", workspaceId, source.id],
      queryFn: async () => {
        const result = await getKnowledgeSourceServer({
          data: {
            workspaceId,
            sourceId: source.id,
          },
        });
        return result as SourceDetail;
      },
    })),
  });

  const snippetRows: SnippetDocumentRow[] = detailQueries.flatMap((query, index) => {
    const source = sources[index];
    const detail = query.data as SourceDetail | undefined;
    if (!source || !detail) return [];
    return (detail.documents ?? []).map((document) => ({ source, document }));
  });

  const normalizedSearchQuery = searchQuery.trim().toLowerCase();
  const filteredRows = snippetRows.filter(({ source, document }) => {
    if (!normalizedSearchQuery) return true;
    const searchable = [
      source.name,
      document.title ?? "",
      document.content ?? "",
      document.source_url ?? "",
      ...(document.tags ?? []),
    ]
      .join(" ")
      .toLowerCase();
    return searchable.includes(normalizedSearchQuery);
  });

  const isRowsLoading =
    isLoading || (sources.length > 0 && detailQueries.some((query) => query.isLoading));

  const { data: selectedSource, isLoading: isDetailLoading } = useQuery<SourceDetail>({
    queryKey: ["knowledge-source-detail", workspaceId, viewSourceId],
    enabled: !!viewSourceId,
    queryFn: async () => {
      if (!viewSourceId) {
        throw new Error("Missing source id");
      }
      const result = await getKnowledgeSourceServer({
        data: {
          workspaceId,
          sourceId: viewSourceId,
        },
      });
      return result as SourceDetail;
    },
  });

  useEffect(() => {
    if (!selectedSource) return;
    setEditName(selectedSource.name ?? "");
    setEditTags(selectedSource.tags ?? []);
    setTagInput("");
    setEditDocuments(
      Object.fromEntries(
        (selectedSource.documents ?? []).map((doc) => [
          doc.id,
          { title: doc.title ?? "", content: doc.content ?? "" },
        ]),
      ),
    );
  }, [selectedSource]);

  const normalizedTags = (tags: string[]) =>
    tags
      .map((tag) => tag.trim())
      .filter((tag) => tag.length > 0)
      .map((tag) => tag.toLowerCase())
      .sort()
      .join("|");

  const hasUnsavedChanges =
    !!selectedSource &&
    (editName.trim() !== (selectedSource.name ?? "") ||
      normalizedTags(editTags) !== normalizedTags(selectedSource.tags ?? []) ||
      (selectedSource.documents ?? []).some((doc) => {
        const draft = editDocuments[doc.id];
        if (!draft) return false;
        return (
          (draft.title ?? "") !== (doc.title ?? "") || (draft.content ?? "") !== (doc.content ?? "")
        );
      }));

  const handleAddTag = () => {
    const next = tagInput.trim();
    if (!next) return;
    const exists = editTags.some((tag) => tag.toLowerCase() === next.toLowerCase());
    if (exists) {
      toast.error("Tag already exists");
      return;
    }
    setEditTags([...editTags, next]);
    setTagInput("");
  };

  const handleRemoveTag = (tag: string) => {
    setEditTags(editTags.filter((t) => t !== tag));
  };

  const handleUpdateDocument = (
    documentId: string,
    patch: Partial<{ title: string; content: string }>,
  ) => {
    setEditDocuments((prev) => ({
      ...prev,
      [documentId]: {
        title: patch.title ?? prev[documentId]?.title ?? "",
        content: patch.content ?? prev[documentId]?.content ?? "",
      },
    }));
  };

  const handleSave = async () => {
    if (!selectedSource || !viewSourceId) return;

    const nextName = editName.trim();
    if (!nextName) {
      toast.error("Please enter a name");
      return;
    }

    setIsSaving(true);
    try {
      const tasks: Promise<unknown>[] = [];

      const shouldUpdateSourceName = nextName !== (selectedSource.name ?? "");
      if (shouldUpdateSourceName) {
        tasks.push(
          updateKnowledgeSourceServer({
            data: { workspaceId, sourceId: viewSourceId, name: nextName },
          }),
        );
      }

      const nextTags = editTags.map((tag) => tag.trim()).filter((tag) => tag.length > 0);

      for (const doc of selectedSource.documents ?? []) {
        const draft = editDocuments[doc.id];
        if (!draft) continue;

        const trimmedTitle = (draft.title ?? "").trim();
        const nextTitle = trimmedTitle.length === 0 ? null : trimmedTitle;

        const shouldUpdateTitle = trimmedTitle !== (doc.title ?? "");
        const shouldUpdateContent = (draft.content ?? "") !== (doc.content ?? "");
        const shouldUpdateTags = normalizedTags(nextTags) !== normalizedTags(doc.tags ?? []);

        if (!shouldUpdateTitle && !shouldUpdateContent && !shouldUpdateTags) continue;

        tasks.push(
          updateKnowledgeDocumentServer({
            data: {
              workspaceId,
              sourceId: viewSourceId,
              documentId: doc.id,
              title: shouldUpdateTitle ? nextTitle : undefined,
              content: shouldUpdateContent ? draft.content : undefined,
              tags: shouldUpdateTags ? nextTags : undefined,
            },
          }),
        );
      }

      if (tasks.length === 0) {
        toast.success("No changes to save");
        return;
      }

      await Promise.all(tasks);

      queryClient.invalidateQueries({
        queryKey: ["knowledge-source-detail", workspaceId, viewSourceId],
      });
      queryClient.invalidateQueries({
        queryKey: ["knowledge-sources", workspaceId, "snippet"],
      });

      toast.success("Snippet updated successfully");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to update snippet");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;

    setIsDeleting(true);
    try {
      await deleteKnowledgeDocumentServer({
        data: {
          workspaceId,
          sourceId: deleteTarget.sourceId,
          documentId: deleteTarget.documentId,
        },
      });
      toast.success(t("messages.documentDeleteSuccess"));
      queryClient.invalidateQueries({
        queryKey: ["knowledge-source-detail", workspaceId, deleteTarget.sourceId],
      });
      queryClient.invalidateQueries({ queryKey: ["knowledge-sources", workspaceId, "snippet"] });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t("messages.documentDeleteFailed"));
    } finally {
      setIsDeleting(false);
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  if (error) {
    return (
      <div className="flex h-full flex-col items-center justify-center p-6">
        <p className="text-red-500">{t("snippetsPage.loadFailed")}</p>
        <Button
          variant="outline"
          className="mt-4"
          onClick={() => queryClient.invalidateQueries({ queryKey: ["knowledge-sources"] })}
        >
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col p-6">
      <div className="mb-6">
        <h1 className="text-xl font-semibold text-gray-900">
          {t("pages:knowledge.sidebar.snippets")}
        </h1>
        <p className="text-sm text-gray-500 mt-1">{t("snippetsPage.subtitle")}</p>
      </div>

      <div className="mb-4 flex items-center gap-4">
        <div className="flex h-10 w-[400px] max-w-[min(400px,40vw)] items-center gap-3 rounded-full border border-transparent bg-muted/60 px-3 transition-colors focus-within:border-blue-500">
          <Search className="size-4 shrink-0 text-muted-foreground" />
          <Input
            placeholder={t("search.placeholder")}
            className="h-8 min-w-0 border-0 bg-transparent px-0 text-sm shadow-none placeholder:text-muted-foreground focus-visible:ring-0"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto overflow-x-hidden rounded-md border border-gray-200 bg-white [&_[data-slot=table-container]]:overflow-x-hidden">
        <Table className="table-fixed">
          <TableHeader>
            <TableRow className="bg-gray-50 hover:bg-gray-50">
              <TableHead className="w-[64%]">{t("websitesPage.pageTitle")}</TableHead>
              <TableHead className="w-[10%]">{t("table.status")}</TableHead>
              <TableHead className="w-[8%]">{t("snippetsPage.size")}</TableHead>
              <TableHead className="w-[14%]">{t("table.lastUpdated")}</TableHead>
              <TableHead className="w-[4%]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isRowsLoading ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin mx-auto text-gray-400" />
                </TableCell>
              </TableRow>
            ) : filteredRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                  {t("snippetsPage.noSnippets")}
                </TableCell>
              </TableRow>
            ) : (
              filteredRows.map(({ source, document }) => {
                const documentTags = document.tags ?? [];
                return (
                  <TableRow
                    key={document.id}
                    className="cursor-pointer hover:bg-gray-50"
                    onClick={() => setViewSourceId(source.id)}
                  >
                    <TableCell className="max-w-0 font-medium text-gray-900">
                      <div className="grid max-w-full grid-cols-[minmax(0,1fr)_max-content] items-center gap-[30px]">
                        <span className="min-w-0 truncate">
                          {document.title || source.name || `Snippet ${document.source_index + 1}`}
                        </span>
                        {documentTags.length > 0 && (
                          <div className="flex shrink-0 justify-start gap-1">
                            {documentTags.slice(0, 1).map((tag: string) => (
                              <span
                                key={tag}
                                className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 border border-gray-200"
                              >
                                {tag}
                              </span>
                            ))}
                            {documentTags.length > 1 && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500 border border-gray-200">
                                +{documentTags.length - 1}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span
                        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          statusColors[source.status]
                        }`}
                      >
                        {source.status}
                      </span>
                    </TableCell>
                    <TableCell>{formatFileSize(document.content_size || 0)}</TableCell>
                    <TableCell>
                      {format(new Date(document.updated_at), "yyyy-MM-dd HH:mm")}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            className="h-8 w-8 p-0"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              setViewSourceId(source.id);
                            }}
                          >
                            {t("snippetsPage.edit")}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            className="text-red-600 focus:text-red-600 focus:bg-red-50"
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteTarget({
                                sourceId: source.id,
                                documentId: document.id,
                              });
                              setDeleteDialogOpen(true);
                            }}
                          >
                            {t("actions.delete")}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className={`${knowledgeDialogContentClass} sm:max-w-[520px]`}>
          <DialogHeader className={knowledgeDialogHeaderClass}>
            <DialogTitle className={knowledgeDialogTitleClass}>
              {t("deleteDocumentDialog.title")}
            </DialogTitle>
            <DialogDescription className="pt-4 text-base text-gray-500">
              {t("deleteDocumentDialog.description")}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className={knowledgeDialogFooterClass}>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              disabled={isDeleting}
              className={knowledgeDialogCancelButtonClass}
            >
              {t("deleteDocumentDialog.cancel")}
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={isDeleting}
              className={knowledgeDialogDangerButtonClass}
            >
              {isDeleting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {t("deleteDocumentDialog.confirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Sheet
        open={viewSourceId !== null}
        onOpenChange={(open) => {
          if (!open) {
            setViewSourceId(null);
          }
        }}
      >
        <SheetContent side="right" className="sm:max-w-xl w-full p-0 gap-0">
          <SheetTitle className="sr-only">
            {editName.trim() || selectedSource?.name || "Snippets"}
          </SheetTitle>
          <div className="border-b px-6 py-5">
            <div className="flex items-center gap-2 text-lg font-normal">
              <ArrowLeft
                className="h-5 w-5 cursor-pointer hover:opacity-70"
                onClick={() => setViewSourceId(null)}
              />
              {editName.trim() || selectedSource?.name || "Snippets"}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-4">
            {isDetailLoading || !selectedSource ? (
              <div className="flex h-full items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
              </div>
            ) : (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-sm font-normal">{t("sourceDetail.name")}</Label>
                  <Input
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    className="border-2 border-gray-300 rounded-lg"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-normal">{t("sourceDetail.tags")}</Label>
                  <div className="border-2 border-gray-300 rounded-lg p-3 min-h-[48px]">
                    <div className="flex flex-wrap gap-2">
                      {editTags.map((tag) => (
                        <Badge
                          key={tag}
                          variant="secondary"
                          className="bg-gray-100 text-gray-800 border-0 rounded-full px-3 py-1 flex items-center gap-2"
                        >
                          {tag}
                          <button
                            type="button"
                            className="hover:text-red-500"
                            onClick={() => handleRemoveTag(tag)}
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </Badge>
                      ))}
                      <Input
                        placeholder={
                          editTags.length === 0 ? t("createSnippet.tagsPlaceholder") : ""
                        }
                        value={tagInput}
                        onChange={(e) => setTagInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            handleAddTag();
                          }
                        }}
                        className="border-0 shadow-none focus-visible:ring-0 px-0 h-auto min-w-[100px] flex-1"
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-normal">{t("sourceDetail.content")}</Label>
                  {selectedSource.documents.length === 0 ? (
                    <div className="text-sm text-gray-500">{t("snippetsPage.noContent")}</div>
                  ) : selectedSource.documents.length === 1 ? (
                    <Textarea
                      value={editDocuments[selectedSource.documents[0]!.id]?.content ?? ""}
                      onChange={(e) =>
                        handleUpdateDocument(selectedSource.documents[0]!.id, {
                          content: e.target.value,
                        })
                      }
                      rows={12}
                      className="border-2 border-gray-300 rounded-lg resize-none font-mono text-sm min-h-[300px]"
                    />
                  ) : (
                    <div className="space-y-3">
                      {selectedSource.documents.map((doc, index) => (
                        <div key={doc.id} className="space-y-2">
                          <Input
                            value={editDocuments[doc.id]?.title ?? ""}
                            onChange={(e) =>
                              handleUpdateDocument(doc.id, { title: e.target.value })
                            }
                            placeholder={doc.title || `Item ${index + 1}`}
                            className="border-2 border-gray-300 rounded-lg"
                          />
                          <Textarea
                            value={editDocuments[doc.id]?.content ?? ""}
                            onChange={(e) =>
                              handleUpdateDocument(doc.id, { content: e.target.value })
                            }
                            rows={20}
                            className="border-2 border-gray-300 rounded-lg resize-none font-mono text-sm min-h-[400px]"
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="border-t px-6 py-4 flex justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => setViewSourceId(null)}
              className="rounded-full px-8"
            >
              {t("createSnippet.cancel")}
            </Button>
            <Button
              type="button"
              onClick={handleSave}
              disabled={!selectedSource || isSaving || !hasUnsavedChanges}
              className="rounded-full px-8 bg-black text-white hover:bg-gray-800"
            >
              {isSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {t("snippetsPage.update")}
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
